// Rich body model for Welcome Day resource "info pages" — the same inline-token
// format the Eng Tour uses, so a resource popup can mix text, images, videos and
// links. The body is stored as one string; a line wrapped in [[ ... ]] is a media
// block placed between the rows of text:
//   [[img|path or url|HEIGHT?]]   inline image (HEIGHT = optional px crop height)
//   [[video|url|autoplay?]]       inline video (YouTube or mp4)
//   [[Label|url]]                 a tappable link button (url optional)
// Everything else is plain text, newlines preserved. The editor works on an
// ordered list of blocks and serializes back to this exact string, so a resource
// whose `details` is plain text just becomes a single text block.

let _id = 0;
const newId = () => ++_id;

// First field can't contain ] or | ; the payload (url/path) may contain spaces.
const TOKEN_RE = /^\s*\[\[\s*([^\]|]+?)\s*(?:\|\s*([^\]]*?)\s*)?\]\]\s*$/;

// Image payload is "src" or "src|HEIGHT" (only a trailing all-digits segment is
// treated as the crop height; width is always 100%).
export function parseImgPayload(payload) {
  const parts = String(payload || '').split('|');
  if (parts.length > 1 && /^\d+$/.test(parts[parts.length - 1].trim())) {
    const height = Number(parts.pop().trim());
    return { src: parts.join('|').trim(), height };
  }
  return { src: String(payload || '').trim(), height: undefined };
}

// Video payload is "url" or "url|autoplay" (URLs never contain "|").
export function parseVideoPayload(payload) {
  const parts = String(payload || '').split('|');
  let autoplay = false;
  if (parts.length > 1 && parts[parts.length - 1].trim().toLowerCase() === 'autoplay') { autoplay = true; parts.pop(); }
  return { url: parts.join('|').trim(), autoplay };
}

export function parseBlocks(text) {
  const blocks = [];
  let buf = [];
  const flush = () => { if (buf.length) { blocks.push({ _id: newId(), type: 'text', value: buf.join('\n') }); buf = []; } };
  String(text || '').split('\n').forEach(line => {
    const m = line.match(TOKEN_RE);
    if (m) {
      flush();
      const kind = m[1].toLowerCase();
      if (kind === 'img') { const { src, height } = parseImgPayload(m[2]); blocks.push({ _id: newId(), type: 'image', src, height }); }
      else if (kind === 'video') { const { url, autoplay } = parseVideoPayload(m[2]); blocks.push({ _id: newId(), type: 'video', url, autoplay }); }
      else blocks.push({ _id: newId(), type: 'link', label: m[1], url: m[2] || '' });
    } else buf.push(line);
  });
  flush();
  return blocks;
}

export function serializeBlocks(blocks) {
  return blocks.map(b => {
    if (b.type === 'link') return `[[${b.label}${b.url ? `|${b.url}` : ''}]]`;
    if (b.type === 'image') return `[[img|${b.src || ''}${b.height ? `|${b.height}` : ''}]]`;
    if (b.type === 'video') return `[[video|${b.url || ''}${b.autoplay ? '|autoplay' : ''}]]`;
    return b.value;
  }).join('\n');
}

export const BLOCK_TYPES = [
  { type: 'text', label: 'Text', emoji: '📝' },
  { type: 'image', label: 'Image', emoji: '🖼' },
  { type: 'video', label: 'Video', emoji: '🎬' },
  { type: 'link', label: 'Link', emoji: '🔗' },
];

export function makeBlock(type) {
  if (type === 'link') return { _id: newId(), type: 'link', label: 'Learn more', url: 'https://' };
  if (type === 'image') return { _id: newId(), type: 'image', src: '' };
  if (type === 'video') return { _id: newId(), type: 'video', url: '', autoplay: false };
  return { _id: newId(), type: 'text', value: '' };
}

// Pull the 11-char id out of any common YouTube URL form; null if not YouTube.
export function youtubeId(url) {
  if (!url) return null;
  const m = String(url).match(/(?:youtube\.com\/(?:watch\?(?:.*&)?v=|embed\/|shorts\/|live\/)|youtu\.be\/)([\w-]{11})/);
  return m ? m[1] : null;
}
